// src/pages/ApprovedBookingsAudit.js
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
// REMOVE static imports for jspdf and jspdf-autotable
// import jsPDF from 'jspdf';
// import 'jspdf-autotable';

// Allowed admin emails
const ALLOWED_ADMIN_EMAILS = [
  'adminwc.raclub@nlngraclub.com',
  'admin@nlngra.com',
  'Richard.Chukwu@nlng.com',
  'emmanuel.nwachukwu@nlng.com'
];

// Helper to get image path
const getImagePath = (path) => {
  if (process.env.NODE_ENV === 'development') {
    return path;
  }
  return `https://nlngraclub.com/${path}`;
};

const ApprovedBookingsAudit = () => {
  const [approvedBookings, setApprovedBookings] = useState([]);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [itemsPerPage] = useState(15);
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    console.log('🔵 ApprovedBookingsAudit mounted');
    checkExistingSession();
  }, []);

  const checkExistingSession = () => {
    console.log('🔵 Checking existing session...');
    const token = localStorage.getItem('nlngra_token');
    const jwt = localStorage.getItem('nlngra_jwt');
    const userStr = localStorage.getItem('nlngra_user');
    
    console.log('Token exists:', !!token);
    console.log('JWT exists:', !!jwt);
    console.log('User exists:', !!userStr);
    
    if ((token || jwt) && userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('User email:', user?.email);
        const isAuthorized = ALLOWED_ADMIN_EMAILS.includes(user.email?.toLowerCase()) || user.account_type === 'admin';
        console.log('Is authorized:', isAuthorized);
        
        if (isAuthorized) {
          setIsAuthenticated(true);
          setCurrentUser(user);
          loadApprovedBookings();
          return;
        } else {
          console.log('User not authorized, logging out');
          logout();
        }
      } catch (e) {
        console.error('Error parsing user:', e);
        logout();
      }
    } else {
      console.log('No session found, showing login modal');
      setShowLoginModal(true);
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('nlngra_token');
    localStorage.removeItem('nlngra_jwt');
    localStorage.removeItem('nlngra_session');
    localStorage.removeItem('nlngra_user');
    localStorage.removeItem('nlngra_dependents');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setShowLoginModal(true);
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError('Please enter both email and password');
      setLoginLoading(false);
      return;
    }
    
    try {
      const url = 'https://nlngraclub.com/nlnapi/index.php?url=login';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword })
      });
      
      const responseText = await response.text();
      let cleanResponse = responseText.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '')
        .replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '')
        .replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      let data = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(cleanResponse.trim());
      
      if (data.success === true) {
        const userEmail = data.user?.email?.toLowerCase();
        const isAuthorized = ALLOWED_ADMIN_EMAILS.includes(userEmail) || data.user?.account_type === 'admin';
        
        if (!isAuthorized) {
          setLoginError('Access restricted to authorized personnel only');
          setLoginLoading(false);
          return;
        }
        
        if (data.token) {
          localStorage.setItem('nlngra_token', data.token);
          localStorage.setItem('nlngra_jwt', data.token);
        }
        if (data.user) {
          localStorage.setItem('nlngra_user', JSON.stringify(data.user));
          setCurrentUser(data.user);
        }
        
        setIsAuthenticated(true);
        setShowLoginModal(false);
        setLoginEmail('');
        setLoginPassword('');
        loadApprovedBookings();
      } else {
        setLoginError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Network error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const loadApprovedBookings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nlngra_token');
      console.log('Loading bookings with token:', !!token);
      
      const response = await fetch('https://nlngraclub.com/nlnapi/bookings.php?action=admin_list&_t=' + Date.now(), {
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      console.log('Bookings response:', data);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let bookings = [];
      if (data && data.success && data.bookings) {
        bookings = data.bookings;
      }
      
      const approved = bookings.filter(b => b.status === 'approved');
      const upcoming = approved.filter(b => {
        const bookingDate = new Date(b.booking_date);
        bookingDate.setHours(0, 0, 0, 0);
        return bookingDate >= today;
      });
      const completed = approved.filter(b => {
        const bookingDate = new Date(b.booking_date);
        bookingDate.setHours(0, 0, 0, 0);
        return bookingDate < today;
      });
      
      const sortByDate = (a, b) => new Date(b.booking_date) - new Date(a.booking_date);
      approved.sort(sortByDate);
      upcoming.sort(sortByDate);
      completed.sort(sortByDate);
      
      setApprovedBookings(approved);
      setUpcomingBookings(upcoming);
      setCompletedBookings(completed);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentBookings = () => {
    switch(activeTab) {
      case 'upcoming': return upcomingBookings;
      case 'completed': return completedBookings;
      default: return approvedBookings;
    }
  };

  const filteredBookings = getCurrentBookings().filter(booking => {
    if (filterType !== 'all' && booking.booking_type !== filterType) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const memberName = `${booking.member_first_name || ''} ${booking.member_last_name || ''}`.toLowerCase();
      const matchesSearch = 
        (booking.booking_number || '').toLowerCase().includes(term) ||
        memberName.includes(term) ||
        (booking.facility_name || '').toLowerCase().includes(term) ||
        (booking.location || '').toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      const bookingDate = new Date(booking.booking_date);
      if (bookingDate < startDate) return false;
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59);
      const bookingDate = new Date(booking.booking_date);
      if (bookingDate > endDate) return false;
    }
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const exportToCSV = () => {
    const headers = ['Booking #', 'Member Name', 'Member Email', 'Type', 'Facility/Equipment', 
                     'Booking Date', 'Start Time', 'End Time', 'Location', 'Guests', 'Status', 'Approved By', 'Approved At'];
    const rows = filteredBookings.map(b => [
      b.booking_number, `${b.member_first_name || ''} ${b.member_last_name || ''}`, b.member_email || b.email,
      b.booking_type, b.facility_name || b.location, b.booking_date,
      b.start_time?.substring(0,5), b.end_time?.substring(0,5), b.location, b.guests || 1,
      new Date(b.booking_date) >= new Date() ? 'Upcoming' : 'Completed',
      b.reviewer_name || 'Admin', new Date(b.approved_at || b.updated_at || b.created_at).toLocaleString()
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `approved_bookings_audit_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToExcel = () => {
    const wsData = filteredBookings.map(b => ({
      'Booking #': b.booking_number,
      'Member Name': `${b.member_first_name || ''} ${b.member_last_name || ''}`,
      'Member Email': b.member_email || b.email,
      'Type': b.booking_type,
      'Facility/Equipment': b.facility_name || b.location,
      'Booking Date': b.booking_date,
      'Start Time': b.start_time?.substring(0,5),
      'End Time': b.end_time?.substring(0,5),
      'Location': b.location,
      'Guests': b.guests || 1,
      'Status': new Date(b.booking_date) >= new Date() ? 'Upcoming' : 'Completed',
      'Approved By': b.reviewer_name || 'Admin',
      'Approved At': new Date(b.approved_at || b.updated_at || b.created_at).toLocaleString()
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Approved Bookings');
    XLSX.writeFile(wb, `approved_bookings_audit_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = async () => {
    try {
      // Dynamic import to avoid loading issues
      const jsPDFModule = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');
      const { default: jsPDF } = jsPDFModule;
      const { default: autoTable } = autoTableModule;
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setTextColor(0, 132, 61);
      doc.text('NLNG RA CLUB - APPROVED BOOKINGS AUDIT REPORT', 14, 22);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleString()} | Total: ${filteredBookings.length} bookings | Status: ${activeTab.toUpperCase()}`, 14, 32);
      
      const tableData = filteredBookings.map(b => [
        b.booking_number || 'N/A',
        `${b.member_first_name || ''} ${b.member_last_name || ''}`.trim() || 'N/A',
        (b.booking_type || 'N/A').toUpperCase(),
        (b.facility_name || b.location) || 'N/A',
        b.booking_date || 'N/A',
        `${b.start_time?.substring(0,5) || ''}-${b.end_time?.substring(0,5) || ''}`,
        b.location || 'N/A',
        new Date(b.booking_date) >= new Date() ? 'UPCOMING' : 'COMPLETED'
      ]);
      
      autoTable(doc, {
        head: [['BOOKING #', 'MEMBER', 'TYPE', 'FACILITY', 'DATE', 'TIME', 'LOCATION', 'STATUS']],
        body: tableData,
        startY: 40,
        styles: { fontSize: 8, cellPadding: 2, textColor: [50, 50, 50] },
        headStyles: { fillColor: [0, 132, 61], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 40, left: 14, right: 14 }
      });
      
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        const footerText = 'RA CLUB CONFIDENTIAL - This document contains confidential club information';
        const textWidth = doc.getTextWidth(footerText);
        const pageWidth = doc.internal.pageSize.width;
        doc.text(footerText, (pageWidth - textWidth) / 2, doc.internal.pageSize.height - 10);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, doc.internal.pageSize.height - 10);
      }
      doc.save(`approved_bookings_audit_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Use Print option instead.');
    }
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Approved Bookings Audit Report</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; background: #fff; }
        .report-header { margin-bottom: 30px; border-bottom: 3px solid #00843D; padding-bottom: 15px; }
        h1 { color: #00843D; font-size: 24px; margin-bottom: 5px; }
        .report-meta { color: #666; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
        th { background: #00843D; color: white; padding: 10px; text-align: left; font-weight: 600; }
        td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; }
        tr:hover { background: #f5f5f5; }
        .status-upcoming { background: #e8f5e9; color: #2e7d32; padding: 4px 8px; border-radius: 12px; font-size: 11px; font-weight: 500; }
        .status-completed { background: #e3f2fd; color: #1565c0; padding: 4px 8px; border-radius: 12px; font-size: 11px; font-weight: 500; }
        .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e0e0e0; text-align: center; font-size: 10px; color: #999; }
        @media print { body { margin: 0; padding: 15px; } }
      </style></head>
      <body>
        <div class="report-header">
          <h1>NLNG RA CLUB - APPROVED BOOKINGS AUDIT REPORT</h1>
          <p class="report-meta">Generated: ${new Date().toLocaleString()} | Total: ${filteredBookings.length} bookings | Status: ${activeTab.toUpperCase()}</p>
        </div>
        <table>
          <thead><tr><th>#</th><th>Booking #</th><th>Member</th><th>Type</th><th>Facility</th><th>Date</th><th>Time</th><th>Location</th><th>Status</th></tr></thead>
          <tbody>${filteredBookings.map((b, i) => `<tr>
            <td>${i + 1}</td>
            <td><strong>${b.booking_number}</strong></td>
            <td>${b.member_first_name || ''} ${b.member_last_name || ''}<br/><small>${b.member_email || b.email}</small></td>
            <td>${b.booking_type}</td>
            <td>${b.facility_name || b.location}</td>
            <td>${b.booking_date ? new Date(b.booking_date).toLocaleDateString() : 'N/A'}</td>
            <td>${b.start_time?.substring(0,5)}-${b.end_time?.substring(0,5)}</td>
            <td>${b.location}</td>
            <td><span class="${new Date(b.booking_date) >= new Date() ? 'status-upcoming' : 'status-completed'}">${new Date(b.booking_date) >= new Date() ? 'Upcoming' : 'Completed'}</span></td>
          </tr>`).join('')}</tbody>
        </table>
        <div class="footer">RA CLUB CONFIDENTIAL - This document contains confidential club information</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
    setFilterType('all');
    setCurrentPage(1);
  };

  // Show login modal if not authenticated
  if (showLoginModal) {
    return (
      <div style={loginStyles.overlay}>
        <div style={loginStyles.modal}>
          <div style={loginStyles.header}>
            <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" style={loginStyles.logoImage} />
            <h2 style={loginStyles.title}>NLNG RA Club</h2>
            <p style={loginStyles.subtitle}>Authorized Access Only</p>
          </div>
          <div style={loginStyles.body}>
            <div style={loginStyles.badge}>🔐 Restricted Area</div>
            <p style={loginStyles.text}>Please enter your credentials to continue.</p>
            {loginError && <div style={loginStyles.error}>{loginError}</div>}
            <form onSubmit={handleLogin} style={loginStyles.form}>
              <div style={loginStyles.field}>
                <label style={loginStyles.label}>Email Address</label>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} style={loginStyles.input} placeholder="Enter your email" required autoFocus />
              </div>
              <div style={loginStyles.field}>
                <label style={loginStyles.label}>Password</label>
                <div style={loginStyles.passwordWrapper}>
                  <input type={showPassword ? "text" : "password"} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} style={loginStyles.input} placeholder="Enter your password" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={loginStyles.toggle}>{showPassword ? '🙈' : '👁️'}</button>
                </div>
              </div>
              <button type="submit" disabled={loginLoading} style={loginStyles.button}>{loginLoading ? 'Verifying...' : 'Sign In'}</button>
            </form>
            <div style={loginStyles.footer}><span>🔒 Secure Connection</span><span>⏱️ Session: 24h</span></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading approved bookings...</p>
      </div>
    );
  }

  // If not authenticated and no login modal (should not happen), show message
  if (!isAuthenticated) {
    return (
      <div style={styles.loadingContainer}>
        <p>Please log in to access this page. <a href="/login">Go to Login</a></p>
      </div>
    );
  }

  const stats = {
    total: approvedBookings.length,
    upcoming: upcomingBookings.length,
    completed: completedBookings.length,
    facilities: approvedBookings.filter(b => b.booking_type === 'facility').length,
    equipment: approvedBookings.filter(b => b.booking_type === 'equipment').length
  };

  return (
    <div style={styles.container}>
      {/* Header Bar */}
      <div style={styles.headerBar}>
        <div style={styles.userInfo}>
          <span style={styles.userIcon}>👤</span>
          <span style={styles.userEmail}>{currentUser?.email}</span>
        </div>
        <button onClick={logout} style={styles.logoutBtn}>
          <span>🚪</span> Logout
        </button>
      </div>

      {/* Logo Section */}
      <div style={styles.logoSection}>
        <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" style={styles.logoImage} />
        <div>
          <h1 style={styles.logoText}>NLNG RA Club</h1>
          <p style={styles.logoSub}>Approved Bookings Audit Trail</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>Total Approved</div><div style={styles.statIcon}>📊</div></div>
        <div style={styles.statCard}><div style={styles.statValue}>{stats.upcoming}</div><div style={styles.statLabel}>Upcoming Events</div><div style={styles.statIcon}>📅</div></div>
        <div style={styles.statCard}><div style={styles.statValue}>{stats.completed}</div><div style={styles.statLabel}>Completed Events</div><div style={styles.statIcon}>✅</div></div>
        <div style={styles.statCard}><div style={styles.statValue}>{stats.facilities}</div><div style={styles.statLabel}>Facility Bookings</div><div style={styles.statIcon}>🏢</div></div>
        <div style={styles.statCard}><div style={styles.statValue}>{stats.equipment}</div><div style={styles.statLabel}>Equipment Loans</div><div style={styles.statIcon}>🔧</div></div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        <button onClick={() => { setActiveTab('all'); setCurrentPage(1); }} style={{...styles.tabBtn, ...(activeTab === 'all' ? styles.tabActive : {})}}>
          <span>📊</span> All Bookings <span style={styles.tabBadge}>{stats.total}</span>
        </button>
        <button onClick={() => { setActiveTab('upcoming'); setCurrentPage(1); }} style={{...styles.tabBtn, ...(activeTab === 'upcoming' ? styles.tabActive : {})}}>
          <span>📅</span> Upcoming <span style={styles.tabBadge}>{stats.upcoming}</span>
        </button>
        <button onClick={() => { setActiveTab('completed'); setCurrentPage(1); }} style={{...styles.tabBtn, ...(activeTab === 'completed' ? styles.tabActive : {})}}>
          <span>✅</span> Completed <span style={styles.tabBadge}>{stats.completed}</span>
        </button>
      </div>

      {/* Filters Bar */}
      <div style={styles.filtersBar}>
        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>🔍</span>
          <input type="text" placeholder="Search by booking #, member, or facility..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={styles.searchInput} />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={styles.select}>
          <option value="all">All Types</option>
          <option value="facility">🏢 Facilities Only</option>
          <option value="equipment">🔧 Equipment Only</option>
        </select>
        <input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} style={styles.dateInput} placeholder="From Date" />
        <input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} style={styles.dateInput} placeholder="To Date" />
        <button onClick={resetFilters} style={styles.resetBtn}>Reset Filters</button>
        <div style={styles.exportGroup}>
          <button onClick={exportToCSV} style={styles.csvBtn}>CSV</button>
          <button onClick={exportToExcel} style={styles.excelBtn}>Excel</button>
          <button onClick={exportToPDF} style={styles.pdfBtn}>PDF</button>
          <button onClick={printReport} style={styles.printBtn}>Print</button>
        </div>
      </div>

      {/* Results Info */}
      {filteredBookings.length > 0 && (
        <div style={styles.resultsInfo}>
          <span>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBookings.length)} of {filteredBookings.length} entries</span>
        </div>
      )}

      {/* Data Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Booking #</th>
              <th style={styles.th}>Member</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Facility/Equipment</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Location</th>
              <th style={styles.th}>Guests</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Approved By</th>
              <th style={styles.th}>Approved At</th>
            </tr>
          </thead>
          <tbody>
            {currentBookings.length === 0 ? (
              <tr>
                <td colSpan="12" style={styles.emptyRow}>
                  <div style={styles.emptyState}>
                    <span style={styles.emptyIcon}>📭</span>
                    <p>No approved bookings found</p>
                    <small>Try adjusting your search or filters</small>
                  </div>
                </td>
              </tr>
            ) : (
              currentBookings.map((booking, idx) => {
                const isCompleted = new Date(booking.booking_date) < new Date();
                return (
                  <tr key={booking.id || idx} style={styles.tableRow}>
                    <td style={styles.td}>{indexOfFirstItem + idx + 1}</td>
                    <td style={{...styles.td, ...styles.bookingNumberCell}}>{booking.booking_number || 'N/A'}</td>
                    <td style={styles.td}>
                      <div style={styles.memberName}>{booking.member_first_name || ''} {booking.member_last_name || ''}</div>
                      <div style={styles.memberEmail}>{booking.member_email || booking.email}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={{...styles.typeBadge, ...(booking.booking_type === 'facility' ? styles.facilityType : styles.equipmentType)}}>
                        {booking.booking_type === 'facility' ? '🏢 Facility' : '🔧 Equipment'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.facilityName}>{booking.facility_name || booking.location || 'N/A'}</div>
                    </td>
                    <td style={styles.td}>{booking.booking_date ? new Date(booking.booking_date).toLocaleDateString() : 'N/A'}</td>
                    <td style={styles.td}>{booking.start_time?.substring(0,5) || ''} - {booking.end_time?.substring(0,5) || ''}</td>
                    <td style={styles.td}>{booking.location || 'N/A'}</td>
                    <td style={styles.td}>{booking.guests || 1}</td>
                    <td style={styles.td}>
                      <span style={{...styles.statusBadge, ...(isCompleted ? styles.completedStatus : styles.upcomingStatus)}}>
                        {isCompleted ? '✅ Completed' : '📅 Upcoming'}
                      </span>
                    </td>
                    <td style={styles.td}>{booking.reviewer_name || 'Admin'}</td>
                    <td style={styles.td}>{new Date(booking.approved_at || booking.updated_at || booking.created_at).toLocaleString()}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {})}}>⏮ First</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {})}}>◀ Prev</button>
          <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {})}}>Next ▶</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {})}}>Last ⏭</button>
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <span style={styles.footerLock}>🔒</span>
          <span style={styles.footerText}>RA CLUB CONFIDENTIAL</span>
          <span style={styles.footerLock}>🔒</span>
        </div>
        <p style={styles.footerNote}>This document contains confidential club information. Unauthorized distribution is prohibited.</p>
        <p style={styles.footerDate}>Generated on {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1600px', margin: '0 auto', padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8fafc' },
  loadingText: { marginTop: '16px', color: '#64748b', fontSize: '14px' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#00843D', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', padding: '12px 20px', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  userIcon: { fontSize: '20px' },
  userEmail: { color: '#00843D' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  logoSection: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '32px', padding: '24px', background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
  logoImage: { width: '70px', height: '70px', objectFit: 'contain', borderRadius: '12px' },
  logoText: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.5px' },
  logoSub: { margin: '6px 0 0', color: '#64748b', fontSize: '14px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' },
  statCard: { position: 'relative', background: 'white', borderRadius: '16px', padding: '20px', textAlign: 'center', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', transition: 'all 0.2s' },
  statValue: { fontSize: '32px', fontWeight: '700', color: '#00843D', marginBottom: '6px' },
  statLabel: { color: '#64748b', fontSize: '13px', fontWeight: '500' },
  statIcon: { position: 'absolute', top: '16px', right: '16px', fontSize: '20px', opacity: 0.5 },
  tabsContainer: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  tabBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '500', color: '#475569', cursor: 'pointer' },
  tabActive: { background: '#00843D', color: 'white', borderColor: '#00843D' },
  tabBadge: { background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '20px', fontSize: '12px', marginLeft: '8px' },
  filtersBar: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', background: 'white', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' },
  searchBox: { flex: 1, minWidth: '250px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '0 14px', borderRadius: '8px', border: '1px solid #e2e8f0' },
  searchIcon: { color: '#94a3b8', fontSize: '14px' },
  searchInput: { flex: 1, padding: '10px 0', border: 'none', background: 'transparent', outline: 'none', fontSize: '14px' },
  select: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', cursor: 'pointer' },
  dateInput: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc' },
  resetBtn: { padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  exportGroup: { display: 'flex', gap: '8px', marginLeft: 'auto' },
  csvBtn: { padding: '10px 16px', background: '#00843D', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  excelBtn: { padding: '10px 16px', background: '#217346', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  pdfBtn: { padding: '10px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  printBtn: { padding: '10px 16px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  resultsInfo: { marginBottom: '16px', fontSize: '13px', color: '#64748b' },
  tableWrapper: { background: 'white', borderRadius: '12px', overflow: 'auto', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '1000px' },
  th: { padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#1e293b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '12px', borderBottom: '1px solid #f1f5f9', color: '#334155', verticalAlign: 'top' },
  tableRow: { transition: 'background 0.2s' },
  bookingNumberCell: { fontFamily: 'monospace', fontWeight: '600', color: '#00843D', fontSize: '12px' },
  memberName: { fontWeight: '600', color: '#1e293b', marginBottom: '2px' },
  memberEmail: { fontSize: '11px', color: '#94a3b8' },
  facilityName: { fontWeight: '500', color: '#1e293b' },
  typeBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  facilityType: { background: '#e0f2fe', color: '#0369a1' },
  equipmentType: { background: '#fef3c7', color: '#b45309' },
  statusBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  upcomingStatus: { background: '#dcfce7', color: '#166534' },
  completedStatus: { background: '#e0e7ff', color: '#3730a3' },
  emptyRow: { textAlign: 'center', padding: '60px 20px' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  emptyIcon: { fontSize: '48px', opacity: 0.5 },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '24px', padding: '16px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  pageBtn: { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  pageBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { padding: '8px 16px', color: '#64748b', fontSize: '13px' },
  footer: { marginTop: '32px', padding: '24px 20px', textAlign: 'center', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  footerContent: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' },
  footerLock: { fontSize: '16px' },
  footerText: { fontSize: '14px', fontWeight: '600', letterSpacing: '1px', color: '#00843D' },
  footerNote: { margin: '8px 0 0', fontSize: '11px', color: '#94a3b8' },
  footerDate: { margin: '8px 0 0', fontSize: '10px', color: '#cbd5e1' }
};

const loginStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 },
  modal: { background: 'white', borderRadius: '24px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { background: 'linear-gradient(135deg, #00843D, #00a54c)', padding: '32px 24px', textAlign: 'center', color: 'white' },
  logoImage: { width: '60px', height: '60px', objectFit: 'contain', marginBottom: '12px', borderRadius: '10px', background: 'white', padding: '5px' },
  title: { fontSize: '24px', fontWeight: '700', margin: '8px 0 0', letterSpacing: '-0.5px' },
  subtitle: { fontSize: '13px', opacity: 0.85, marginTop: '6px' },
  body: { padding: '32px' },
  badge: { display: 'inline-block', background: '#fef3c7', color: '#d97706', padding: '6px 14px', borderRadius: '30px', fontSize: '12px', fontWeight: '600', marginBottom: '20px' },
  text: { fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: '1.5', textAlign: 'center' },
  error: { background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px', textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#1e293b' },
  input: { padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', transition: 'all 0.2s' },
  passwordWrapper: { position: 'relative' },
  toggle: { position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', opacity: 0.6 },
  button: { background: 'linear-gradient(135deg, #00843D, #00a54c)', color: 'white', border: 'none', padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  footer: { marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8' }
};

// Add the spin animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
if (!document.head.querySelector('#approved-audit-styles')) {
  styleSheet.id = 'approved-audit-styles';
  document.head.appendChild(styleSheet);
}

export default ApprovedBookingsAudit;